import { Pause, Play, Radio, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Locale, TimelinePayload } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";

type StickyEmbed = NonNullable<
  NonNullable<TimelinePayload["stickyMedia"]>["embed"]
>;

/**
 * Player-iframe: wird imperativ gesetzt und nur bei echtem HTML-Wechsel
 * neu injiziert. dangerouslySetInnerHTML würde bei jedem Timeline-Update
 * (neue Beiträge) die iframe neu laden und den Player stoppen.
 */
function EmbedFrame({ html, className }: { html: string; className: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const lastHtml = useRef<string | null>(null);
  useEffect(() => {
    if (hostRef.current && lastHtml.current !== html) {
      lastHtml.current = html;
      hostRef.current.innerHTML = html;
    }
  }, [html]);
  return <div ref={hostRef} className={className} />;
}

export function StickyAudio({
  media,
  locale,
  inline = false,
}: {
  media: NonNullable<TimelinePayload["stickyMedia"]>;
  locale: Locale;
  inline?: boolean;
}) {
  const embed: StickyEmbed | null = media.embed;
  const [visible, setVisible] = useState(true);
  const [loaded, setLoaded] = useState(false);

  if (!visible || !embed) return null;

  const startPlayback = () => setLoaded(true);

  const label =
    locale === "en" ? "Start external player" : "Externen Player starten";

  if (inline) {
    return (
      <figure className="pulse-sticky" aria-label={media.title}>
        <p className="pulse-sticky__label">{t(locale, "liveVideo")}</p>
        {embed.html ? (
          loaded ? (
            <EmbedFrame
              html={embed.html}
              className="sticky-audio__frame sticky-audio__frame--inline"
            />
          ) : (
            <button
              type="button"
              className="sticky-audio__teaser"
              onClick={startPlayback}
              aria-label={label}
              title={label}
            >
              <Play size={19} fill="currentColor" />
              <strong>{media.title}</strong>
              <span className="sticky-audio__hint">
                {locale === "en"
                  ? "External player – loads after tap."
                  : "Externer Player – lädt nach Tap."}
              </span>
            </button>
          )
        ) : (
          <a
            className="sticky-audio__teaser"
            href={embed.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Play size={19} fill="currentColor" />
            <strong>{media.title}</strong>
          </a>
        )}
        {media.title && <figcaption>{media.title}</figcaption>}
      </figure>
    );
  }

  return (
    <aside className="sticky-audio" aria-label={media.title}>
      <span className="sticky-audio__signal">
        <Radio size={18} />
      </span>
      <div>
        <small>
          {loaded
            ? embed.provider
            : locale === "en"
              ? "Live audio"
              : "Live Audio"}
        </small>
        <strong>{media.title}</strong>
        {!loaded && (
          <span className="sticky-audio__hint">
            {locale === "en"
              ? "External player – loads after tap."
              : "Externer Player – lädt nach Tap."}
          </span>
        )}
      </div>
      {embed.html ? (
        loaded ? (
          <EmbedFrame html={embed.html} className="sticky-audio__frame" />
        ) : (
          <button
            type="button"
            onClick={startPlayback}
            aria-label={label}
            title={label}
          >
            <Play size={19} fill="currentColor" />
          </button>
        )
      ) : (
        <a href={embed.url} target="_blank" rel="noopener noreferrer">
          <Play size={19} fill="currentColor" />
        </a>
      )}
      {loaded && embed.html && (
        <button
          type="button"
          className="sticky-audio__stop"
          onClick={() => setLoaded(false)}
          aria-label={
            locale === "en" ? "Stop audio player" : "Audioplayer stoppen"
          }
        >
          <Pause size={19} fill="currentColor" />
        </button>
      )}
      <button
        type="button"
        className="sticky-audio__close"
        onClick={() => setVisible(false)}
        aria-label={
          locale === "en" ? "Close audio player" : "Audioplayer schließen"
        }
      >
        <X size={16} />
      </button>
    </aside>
  );
}
